import { Mic, MicOff, Loader2 } from 'lucide-react'
import { useImperativeHandle, forwardRef } from 'react'
import { useVoiceTranscription } from '@/shared/hooks/useVoiceTranscription'

interface MicButtonProps {
  onTranscript: (text: string) => void
  className?: string
  iconSize?: number
  ghost?: boolean
}

export interface MicButtonHandle {
  toggle: () => void
}

export const MicButton = forwardRef<MicButtonHandle, MicButtonProps>(
  function MicButton({ onTranscript, className = '', iconSize = 18, ghost = false }, ref) {
    const { isRecording, isTranscribing, error, startRecording, stopRecording } =
      useVoiceTranscription({ onTranscript })

    const handleClick = () => {
      if (isRecording) stopRecording()
      else void startRecording()
    }

    useImperativeHandle(ref, () => ({
      toggle: handleClick
    }))

    const baseClass =
      'inline-flex items-center justify-center rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:opacity-50 disabled:cursor-not-allowed p-2 max-sm:p-2.5'

    const idleClass = ghost
      ? 'text-[#888888] hover:text-[#EEEEEE] hover:bg-white/5'
      : 'border border-white/10 text-[#888888] hover:text-[#EEEEEE] hover:bg-white/5'

    const recordingClass = 'text-red-400 animate-pulse'
    const transcribingClass = 'text-[#888888]'

    let stateClass = idleClass
    if (isRecording) stateClass = recordingClass
    else if (isTranscribing) stateClass = transcribingClass

    let Icon = Mic
    if (isRecording) Icon = MicOff

    // Detect OS for keyboard shortcut hint
    const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0
    const shortcutHint = isMac ? '⌘M' : 'Ctrl+M'
    const titleText = isRecording
      ? 'Stop recording'
      : isTranscribing
      ? 'Transcribing…'
      : `Voice input (${shortcutHint})`

    return (
      <div className="relative inline-flex flex-col items-center">
        <button
          type="button"
          onClick={handleClick}
          disabled={isTranscribing}
          className={`${baseClass} ${stateClass} ${className}`}
          title={error ?? titleText}
          aria-label={error ?? titleText}
        >
          {isTranscribing ? (
            <Loader2 size={iconSize} className="animate-spin" />
          ) : (
            <Icon size={iconSize} />
          )}
        </button>
        {error && (
          <span className="absolute top-full mt-1 whitespace-nowrap rounded bg-black/80 px-2 py-1 text-xs text-red-400">
            {error}
          </span>
        )}
      </div>
    )
  }
)
